import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Accordion, AccordionSummary, AccordionDetails, Tooltip, makeStyles } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import BasicTable from '../../../../../atoms/BasicTable'
import { useFormattingContext } from '../../../../../organisms/FormattingProvider/FormattingContext'
import Markup from '../../../../../atoms/Markup'
import Icon from '../../../../../atoms/Icon'

const useStyles = makeStyles(() => ({
  accordion: {
    backgroundColor: 'var(--surface-bg-color-d1)',
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-sm)',
    boxShadow: 'none',
    marginBottom: 'var(--margin-sm)',
    '&:before': {
      display: 'none'
    },
    '&.Mui-expanded': {
      margin: '0 0 var(--margin-sm) 0'
    }
  },
  summary: {
    backgroundColor: 'var(--surface-bg-color-d1)',
    minHeight: '48px',
    '&.Mui-expanded': {
      minHeight: '48px'
    }
  },
  summaryContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-md)',
    '&.Mui-expanded': {
      margin: '12px 0'
    }
  },
  details: {
    padding: 'var(--padding-md)',
    backgroundColor: 'var(--surface-bg-color-d0)'
  },
  outputName: {
    fontWeight: 'var(--text-weight-bold)',
    fontSize: 'var(--text-size-md)'
  },
  badge: {
    backgroundColor: 'var(--color-accent-500)',
    color: 'var(--color-contrast-accent-500)',
    padding: '2px 8px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 'var(--text-weight-bold)'
  },
  descriptionWrap: {
    '&.MuiTooltip-popper': {
      '& .MuiTooltip-tooltipPlacementTop, & .MuiTooltip-tooltipPlacementBottom': {
        minWidth: '30vw',
        backgroundColor: 'var(--theme-color-surface-d0)',
        color: 'var(--text-color-main)',
        border: 'var(--border-neutral-emphasis)',
        boxShadow: 'var(--shadow-sm)',
        fontSize: 'var(--font-size-sm)',
        padding: 'var(--padding-sm) var(--padding-md)'
      }
    }
  }
}))

function ApiOutputsAccordion ({ apiOutputs }) {
  const classes = useStyles()
  const { formatter } = useFormattingContext()
  const tooltipTitle = useCallback((description) => {
    return (
      <Markup type='markdown' value={description} />
    )
  }, [])

  if (!apiOutputs || apiOutputs.length === 0) {
    return (
      <div style={{ padding: 'var(--padding-md)', color: 'var(--text-color-subtle)' }}>
        No API outputs defined
      </div>
    )
  }

  return (
    <div>
      {apiOutputs.map((output) => (
        <Accordion
          key={`output_${output.catalogApiOutputId || output.outputName}`}
          className={classes.accordion}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.summary}
            classes={{ content: classes.summaryContent }}
          >
            <span className={classes.outputName}>{output.outputName}</span>
            <span style={{ color: 'var(--text-color-subtle)' }}>—</span>
            <span>{output.displayName}</span>
            {output.isDefault && (
              <span className={classes.badge}>DEFAULT</span>
            )}
            {output.fields && output.fields.length > 0 && (
              <span style={{ color: 'var(--text-color-subtle)', fontSize: 'var(--text-size-sm)' }}>
                ({output.fields.length} {output.fields.length === 1 ? 'field' : 'fields'})
              </span>
            )}
          </AccordionSummary>
          <AccordionDetails className={classes.details}>
            <div style={{ width: '100%' }}>
              {output.description && (
                <div style={{ marginBottom: 'var(--margin-md)', color: 'var(--text-color-main)' }}>
                  <Markup type='markdown' value={output.description} />
                </div>
              )}

              {output.fields && output.fields.length > 0 ? (
                <BasicTable className='opal-presentation-table zebra-rows secondary'>
                  <thead className='opt-thead'>
                    <tr className='opt-tr'>
                      <th className='opt-th'>Path</th>
                      <th className='opt-th'>Display Name</th>
                      <th className='opt-th'>Data Type</th>
                      <th className='opt-th'>Nullable</th>
                      <th className='opt-th'>PII</th>
                      <th className='opt-th'>AI</th>
                      <th className='opt-th'>Ordinal</th>
                    </tr>
                  </thead>
                  <tbody className='opt-tbody'>
                    {output.fields.map((field) => (
                      <tr className='opt-tr' key={`field_${field.catalogApiOutputFieldId || field.path}`}>
                        <td className='opt-td'>
                          {field.description ? (
                            <Tooltip
                              title={tooltipTitle(field.description)}
                              classes={{
                                tooltip: 'big_tooltip',
                                popper: classes.descriptionWrap
                              }}
                            >
                              <div className='flex flex-row gap-sm items-center'>
                                <span style={{ fontFamily: 'var(--font-mono)' }}>{field.path}</span>
                                <Icon name='info' customSize={16} />
                              </div>
                            </Tooltip>
                          ) : (
                            <span style={{ fontFamily: 'var(--font-mono)' }}>{field.path}</span>
                          )}
                        </td>
                        <td className='opt-td'>{field.displayName}</td>
                        <td className='opt-td'>{field.dataType}</td>
                        <td className='opt-td'>{formatter(!!field.isNullable, 'bool')}</td>
                        <td className='opt-td'>{formatter(!!field.isPii, 'bool')}</td>
                        <td className='opt-td'>{formatter(!!field.isAiEnabled, 'bool')}</td>
                        <td className='opt-td'>{field.ordinal}</td>
                      </tr>
                    ))}
                  </tbody>
                </BasicTable>
              ) : (
                <div style={{ color: 'var(--text-color-subtle)', fontSize: 'var(--text-size-sm)' }}>
                  No fields defined
                </div>
              )}
            </div>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  )
}

ApiOutputsAccordion.propTypes = {
  apiOutputs: PropTypes.arrayOf(PropTypes.shape({
    catalogApiOutputId: PropTypes.number,
    outputName: PropTypes.string,
    displayName: PropTypes.string,
    schemaDefinition: PropTypes.object,
    description: PropTypes.string,
    isDefault: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    fields: PropTypes.arrayOf(PropTypes.shape({
      catalogApiOutputFieldId: PropTypes.number,
      path: PropTypes.string,
      displayName: PropTypes.string,
      dataType: PropTypes.string,
      isNullable: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
      isPii: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
      isAiEnabled: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
      ordinal: PropTypes.number,
      description: PropTypes.string
    }))
  }))
}

export default ApiOutputsAccordion
